/**
 * Billing & Usage Page
 *
 * Manage subscription, view usage stats, and access invoices.
 * Owner-only access.
 */

import { useState, useEffect } from 'react';
import {
  HardDrive,
  Table2,
  Users,
  Receipt,
  ExternalLink,
  Download,
  Loader2,
  XCircle,
  RotateCcw,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@loader/ui';
import type { SubscriptionStatus } from '@loader/shared-types';
import { BILLING_CONSTANTS, CENTS_PER_DOLLAR } from '@loader/shared-types';

import { SettingsPageLayout } from '../components';
import { MS_PER_SECOND } from './constants';
import { UsageRadialCard } from './components';

import { UpgradeDialog } from '@/components/billing';
import { useSubscription, useCheckoutSession, useWorkspaceMembership, useWorkspace } from '@/hooks';
import { formatBytes } from '@/lib/formatters';

/**
 * Get status badge variant
 */
function getStatusBadgeVariant(
  status: SubscriptionStatus | '',
  isCanceling?: boolean
): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (isCanceling) return 'destructive';
  switch (status) {
    case 'active':
      return 'default';
    case 'trialing':
      return 'secondary';
    case 'past_due':
    case 'incomplete':
      return 'destructive';
    case 'canceled':
      return 'outline';
    default:
      return 'outline';
  }
}

/**
 * Get human-readable status text
 */
function getStatusText(
  status: SubscriptionStatus | '',
  days: number | null,
  isCanceling?: boolean
): string {
  if (isCanceling && days !== null) {
    return `Cancels in ${days} days`;
  }
  switch (status) {
    case 'active':
      return 'Active';
    case 'trialing':
      return days !== null ? `Trial (${days} days left)` : 'Trial';
    case 'past_due':
      return 'Past Due';
    case 'incomplete':
      return 'Payment Required';
    case 'canceled':
      return 'Canceled';
    default:
      return status;
  }
}

export function BillingPage() {
  const { isOwner, isLoading: isMembershipLoading } = useWorkspaceMembership();
  const {
    workspace,
    storage: storageInfo,
    tables: tablesInfo,
    members: membersInfo,
    isLoading: workspaceLoading,
  } = useWorkspace();
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);

  const workspaceId = workspace?.id ?? '';

  // Subscription data
  const {
    plan: subscriptionPlan,
    status,
    isTrial,
    isExpired,
    daysRemaining,
    workspace: subscriptionWorkspace,
    cancelSubscription,
    isCanceling,
    reactivateSubscription,
    isReactivating,
    hasStripeSubscription,
    invoices,
    isLoadingInvoices,
    fetchInvoices,
  } = useSubscription(workspaceId);

  // Checkout session for upgrades
  const { createCheckout, changePlan } = useCheckoutSession(workspaceId, {
    onSuccess: () => {
      setLoadingPlanId(null);
      setUpgradeDialogOpen(false);
    },
    onError: () => {
      setLoadingPlanId(null);
    },
  });

  // Fetch invoices on mount
  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  return (
    <SettingsPageLayout
      title="Billing"
      description="Manage your subscription and monitor resource usage"
      isLoading={workspaceLoading || isMembershipLoading}
      isOwner={isOwner}
      requiresOwner={true}
    >
      {/* Current Plan */}
      <Card>
        <CardContent className="space-y-4">
          {subscriptionWorkspace ? (
            <>
              <div className="flex items-center justify-between">
                {/* Plan info */}
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="text-xl font-semibold">
                      {subscriptionPlan?.name || 'Unknown Plan'}
                    </h3>
                    <Badge variant={getStatusBadgeVariant(status?.status || '', status?.isCanceling)}>
                      {getStatusText(status?.status || '', daysRemaining, status?.isCanceling)}
                    </Badge>
                  </div>
                  {subscriptionPlan?.monthly_price_cents && (
                    <p className="text-sm text-muted-foreground">
                      ${(subscriptionPlan.monthly_price_cents / CENTS_PER_DOLLAR).toFixed(0)}/month
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => setUpgradeDialogOpen(true)}>
                    Change Plan
                  </Button>
                  {status?.status === 'active' && !isTrial && !status?.isCanceling && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" disabled={isCanceling}>
                          {isCanceling ? (
                            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                          ) : (
                            <XCircle className="mr-1 h-4 w-4" />
                          )}
                          Cancel
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Cancel Subscription</AlertDialogTitle>
                          <AlertDialogDescription>
                            Your subscription will remain active until the end of your billing period.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={cancelSubscription}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Yes, Cancel
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  {status?.status === 'active' && status?.isCanceling && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={reactivateSubscription}
                      disabled={isReactivating}
                    >
                      {isReactivating ? (
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      ) : (
                        <RotateCcw className="mr-1 h-4 w-4" />
                      )}
                      Reactivate
                    </Button>
                  )}
                </div>
              </div>

              {/* Trial expired */}
              {isTrial && isExpired && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm dark:border-red-900 dark:bg-red-950/20">
                  <p className="font-medium text-red-800 dark:text-red-200">
                    Trial expired
                  </p>
                  <p className="text-red-700 dark:text-red-300">
                    Upgrade now to regain access to your workspace
                  </p>
                </div>
              )}

              {/* Trial ending soon */}
              {isTrial && !isExpired && daysRemaining !== null && daysRemaining <= BILLING_CONSTANTS.TRIAL_WARNING_DAYS && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/20">
                  <p className="font-medium text-amber-800 dark:text-amber-200">
                    Trial ending soon
                  </p>
                  <p className="text-amber-700 dark:text-amber-300">
                    Upgrade now to keep access to your data
                  </p>
                </div>
              )}

              {/* Past due warning */}
              {status?.status === 'past_due' && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm dark:border-red-900 dark:bg-red-950/20">
                  <p className="font-medium text-red-800 dark:text-red-200">Payment failed</p>
                  <p className="text-red-700 dark:text-red-300">
                    Please update your payment method
                  </p>
                </div>
              )}
            </>
          ) : (
            <p className="py-4 text-center text-muted-foreground">No active subscription</p>
          )}
        </CardContent>
      </Card>

      {/* Usage */}
      <div className="grid gap-4 sm:grid-cols-3">
        {storageInfo && (
          <UsageRadialCard
            label="Storage"
            icon={HardDrive}
            usage={storageInfo.usage}
            limit={storageInfo.limit}
            percentUsed={storageInfo.percent_used}
            formatValue={(v) => formatBytes(v)}
          />
        )}
        {tablesInfo && (
          <UsageRadialCard
            label="Tables"
            icon={Table2}
            usage={tablesInfo.usage}
            limit={tablesInfo.limit}
            percentUsed={tablesInfo.percent_used}
            formatValue={(v) => String(v)}
          />
        )}
        {membersInfo && (
          <UsageRadialCard
            label="Members"
            icon={Users}
            usage={membersInfo.usage}
            limit={membersInfo.limit}
            percentUsed={membersInfo.percent_used}
            formatValue={(v) => String(v)}
          />
        )}
      </div>

      {/* Invoices */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Invoices
          </CardTitle>
          <CardDescription>View and download your billing history</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingInvoices ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : invoices.length === 0 ? (
            <p className="py-4 text-center text-muted-foreground">No invoices yet</p>
          ) : (
            <div className="space-y-2">
              {invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{invoice.number || invoice.id}</span>
                      <Badge
                        variant={
                          invoice.status === 'paid'
                            ? 'default'
                            : invoice.status === 'open'
                              ? 'secondary'
                              : 'outline'
                        }
                      >
                        {invoice.status}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {new Date(invoice.created * MS_PER_SECOND).toLocaleDateString()} •{' '}
                      {(invoice.amount_paid / CENTS_PER_DOLLAR).toLocaleString('en-US', {
                        style: 'currency',
                        currency: invoice.currency.toUpperCase(),
                      })}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {invoice.hosted_invoice_url && (
                      <Button size="sm" variant="ghost" asChild>
                        <a
                          href={invoice.hosted_invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="View invoice"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                    {invoice.invoice_pdf && (
                      <Button size="sm" variant="ghost" asChild>
                        <a
                          href={invoice.invoice_pdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Download PDF"
                        >
                          <Download className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upgrade Dialog */}
      <UpgradeDialog
        context="upgrade"
        open={upgradeDialogOpen}
        onOpenChange={setUpgradeDialogOpen}
        loadingPlanId={loadingPlanId}
        currentPlanId={subscriptionPlan?.id}
        isTrial={isTrial}
        onUpgrade={(planId, billingInterval) => {
          setLoadingPlanId(planId);
          if (hasStripeSubscription) {
            changePlan({ planId, billingInterval });
          } else {
            createCheckout({ planId, billingInterval });
          }
        }}
      />
    </SettingsPageLayout>
  );
}
